/**
 * Thin wrappers around the voiceprint commands exposed by
 * tauri-plugin-voice.
 *
 * Callers get back summary metadata only. The raw 192-dim embedding
 * vector lives exclusively on-device, stored in
 * `Library/Application Support/Meridian/voiceprints.json`. The IPC
 * layer never returns it — we don't want biometric material sitting
 * in the JS heap.
 *
 * On desktop and web these resolve to deterministic stubs so features
 * that call them compile and render.
 */
import { invoke } from "@tauri-apps/api/core";

import type { VoiceprintSummary } from "@/types/voice";

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

/**
 * Embed each audio file at the given paths, average the embeddings,
 * L2-normalize, and persist the result keyed by `id`. Returns summary
 * stats about the stored voiceprint.
 */
export async function computeVoiceprint(
  paths: string[],
  id: string,
  contactId?: string | null
): Promise<VoiceprintSummary> {
  if (!isNative) {
    return {
      id,
      contactId: contactId ?? null,
      dim: 192,
      sampleCount: paths.length,
      modelVersion: "desktop-stub",
      enrolledAt: new Date().toISOString(),
    };
  }
  return invoke<VoiceprintSummary>("plugin:voice|compute_voiceprint", {
    paths,
    id,
    contactId: contactId ?? null,
  });
}

export async function getVoiceprint(
  id: string
): Promise<VoiceprintSummary | null> {
  if (!isNative) return null;
  const result = await invoke<VoiceprintSummary | null>(
    "plugin:voice|get_voiceprint",
    { id }
  );
  return result ?? null;
}
